/**
 * FoliarRoundEditorPage — round *definition* editor (Task 8).
 *
 * Split out of NutritionProgrammeFormPage's 1,436-line round/group/product
 * workbench: this page edits ONE round (name, month, planned start,
 * water rate, orchard groups, product dose lines) at
 * `foliar/programmes/:programmeId/rounds/:roundId/edit`. Routing is wired by
 * a later task — this file only exports the page component.
 *
 * Two components live in this file:
 *  - `FoliarRoundEditorPage` (default export) — resolves `:programmeId` /
 *    `:roundId` against the loaded programme, owns the breadcrumb (which must
 *    render in every state: loading, load-error, not-found, ready) and the
 *    not-found guard (#450's failure mode — never a blank screen).
 *  - `RoundEditor` — the actual form, mounted only once the round is known to
 *    exist. Splitting it out lets `useFormDirty`'s "capture the first value
 *    forever" ref do the right thing: `initialRound` is already the real,
 *    loaded round on RoundEditor's first render, instead of `null` (which is
 *    what a single fetch-in-place component would hand it on mount, wrongly
 *    pinning "dirty" comparisons against `null` for the rest of the page's
 *    life). This mirrors SprayProgramForm's container/presentational split.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageHeader from '../shell/PageHeader';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import { formatMonthName } from '../../dateFormat';
import {
  Field,
  FormSection,
  FormShell,
  FormShellSection,
  Modal,
  Notice,
  SaveBar,
  SaveBarStatus,
  TabBar,
  TabPanel,
} from '../patterns';
import { useFormDirty } from '../../hooks/useFormDirty';
import { useBeforeUnloadWarning } from '../../hooks/useBeforeUnloadWarning';
import {
  nutritionService,
  NutritionProgramme,
  FertilizerProduct,
  SoilRound,
} from '../../services/nutritionService';
import { buildRound, EditableGroup, EditableProductLine, EditableRound } from './nutritionProgrammePayload';
import { availableOrchards, canSaveRound, hydrateRound, nextGroupId, roundEditorErrors } from './foliarRoundEditor';
import { UNITS_BY_FAMILY, MEASURE_UNITS, familyOf } from '../../utils/units';
import './nutrition.css';

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

interface OrchardOption {
  _id: string;
  orchardName: string;
}

const programmeEditorPath = (programmeId: string) => `/nutrition/foliar/setup/programmes/${programmeId}`;

/**
 * Hydrate for the FOLIAR editor: every round on this page is a foliar round by
 * definition, so the application type is fixed rather than offered. Forcing it
 * here (not at the save call) keeps one source of truth — the dirty baseline,
 * the default dose basis and the emitted payload all read the same value, so a
 * legacy soil-typed round opens clean instead of instantly "dirty". A boot
 * migration converts the stored soil-typed rounds; this is the UI half.
 */
const hydrateFoliarRound = (round: SoilRound): EditableRound => ({
  ...hydrateRound(round),
  type: 'foliar',
});

// ── outer: resolve params against the loaded programme ─────────────────────
const FoliarRoundEditorPage: React.FC = () => {
  const { t } = useTranslation();
  const { programmeId, roundId } = useParams<{ programmeId: string; roundId: string }>();

  const [programme, setProgramme] = useState<NutritionProgramme | null>(null);
  const [loadError, setLoadError] = useState('');
  // Single source of truth for orchards/fertilizers: this outer component owns
  // both permanently (never copied into a child `useState` initialiser). Three
  // independent fetches race here (orchards, fertilizers, programme) with no
  // ordering between them; RoundEditor only mounts once the programme resolves,
  // which can happen BEFORE orchards/fertilizers do. A `useState` INITIALISER
  // in the child would capture whatever those props were at that moment —
  // possibly still `[]` — and ignore every later update forever. Reading the
  // live prop on every render (no child-side copy) means RoundEditor always
  // reflects whichever of the two fetches finishes last.
  const [orchards, setOrchards] = useState<OrchardOption[]>([]);
  const [orchardsError, setOrchardsError] = useState('');
  const [fertilizers, setFertilizers] = useState<FertilizerProduct[]>([]);
  const [fertilizersError, setFertilizersError] = useState('');

  // ── load orchards (copied verbatim from NutritionProgrammeFormPage:103-116) ──
  useEffect(() => {
    axios
      .get<OrchardOption[]>('/api/v1/orchards', { headers: authHeaders() })
      .then((res) =>
        setOrchards(
          (res.data ?? []).map((o) => ({
            _id: (o as { _id?: string; id?: string })._id ?? (o as { id?: string }).id ?? '',
            orchardName: o.orchardName,
          })),
        ),
      )
      .catch(() => {
        setOrchards([]);
        // A failed fetch must not read the same as "no orchards yet" — that
        // symptom is indistinguishable from the race the Critical fix above
        // closes, and would be very hard to diagnose in the field otherwise.
        setOrchardsError(t('nutrition.foliar.round.orchardsLoadError'));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── load fertilizer catalog ──────────────────────────────────────────────
  useEffect(() => {
    nutritionService
      .listFertilizers()
      .then(setFertilizers)
      .catch(() => {
        setFertilizers([]);
        setFertilizersError(t('nutrition.foliar.round.fertilizersLoadError'));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── load the programme this round belongs to ─────────────────────────────
  useEffect(() => {
    if (!programmeId || !roundId) return;
    let cancelled = false;
    nutritionService
      .getProgramme(programmeId)
      .then((p) => {
        if (!cancelled) setProgramme(p);
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [programmeId, roundId]);

  const foundRound = programme?.rounds?.find((r) => r.roundId === roundId) ?? null;

  // Rebuild the workspace's ?programme= from the programme being edited: this
  // page is a SIBLING of FoliarWorkspaceLayout, not one of its tabs, so it
  // never receives the layout's resolved programme. A bare '/nutrition/foliar'
  // would silently move the user to whichever programme is active. Hooks must
  // run unconditionally, so this sits above every early return below — every
  // render state (loading / load-error / not-found / ready) gets a correct
  // trail, never a stale one left over from the previous page.
  useBreadcrumb([
    { label: 'Citrus Synch', to: '/' },
    { label: t('nutrition.title'), to: '/nutrition' },
    {
      label: t('nutrition.foliar.workspace.title'),
      to: programmeId ? `/nutrition/foliar?programme=${programmeId}` : '/nutrition/foliar',
    },
    { label: t('nutrition.foliar.crumb.setup'), to: '/nutrition/foliar/setup' },
    { label: programme?.season || t('common.loading') },
    { label: foundRound ? foundRound.name : t('nutrition.foliar.round.crumb') },
  ]);

  // Error path 1: the programme itself failed to load (network/server error).
  // No modal can be open at this point (the form hasn't rendered yet), so a
  // page-level Notice is visible without GC1's stacking-context hazard.
  if (loadError) {
    return (
      <div data-testid="foliar-round-editor-page">
        <Notice tone="danger" testId="fre-load-error">
          {loadError}
        </Notice>
      </div>
    );
  }

  if (!programme) {
    return <div data-testid="foliar-round-editor-page">{t('common.loading')}</div>;
  }

  // Error path 2: a roundId not present in the loaded programme (#450's
  // failure mode) — never a blank screen. A link back to the programme editor
  // rides Notice's `action` slot rather than a bespoke PageHeader button.
  if (!foundRound) {
    return (
      <div data-testid="foliar-round-editor-page">
        <Notice
          tone="danger"
          testId="fre-not-found"
          action={
            <Link
              to={programmeEditorPath(programmeId as string)}
              className="pat-btn pat-btn--outline"
            >
              {t('nutrition.foliar.round.backToProgramme')}
            </Link>
          }
        >
          {t('nutrition.foliar.round.notFound')}
        </Notice>
      </div>
    );
  }

  return (
    <div data-testid="foliar-round-editor-page">
      {/* Error path 5 / GC1: these are PAGE-LEVEL Notices. Their position in
          the tree implies nothing about stacking — `Modal` portals to
          `document.body`, so a modal rendered "below" them still paints above
          them — and both errors are set ASYNCHRONOUSLY, so either can land
          while Bulk add or the quick-create modal is already open. The
          concrete case: the orchards fetch rejects late, the user opens Bulk
          add, and the modal's "No orchards available." empty state is shown
          with its only explanation sitting underneath the scrim. That residual
          case is knowingly accepted — the page-level Notice is a deferred
          item, and both fetches normally settle before any modal opens. */}
      {orchardsError && (
        <Notice tone="danger" testId="fre-orchards-error">
          {orchardsError}
        </Notice>
      )}
      {fertilizersError && (
        <Notice tone="danger" testId="fre-fertilizers-error">
          {fertilizersError}
        </Notice>
      )}
      <RoundEditor
        programmeId={programmeId as string}
        programme={programme}
        initialRound={foundRound}
        orchards={orchards}
        fertilizers={fertilizers}
        onFertilizerCreated={(f) =>
          setFertilizers((prev) => (prev.some((x) => x._id === f._id) ? prev : [...prev, f]))
        }
      />
    </div>
  );
};

export default FoliarRoundEditorPage;

// ── inner: the actual form, mounted only once `initialRound` is real ───────
interface RoundEditorProps {
  programmeId: string;
  programme: NutritionProgramme;
  initialRound: SoilRound;
  // Live props, NOT copied into local state (see the container's comment
  // above `<RoundEditor>`): the orchards/fertilizers fetches race the
  // programme fetch, and a `useState` initialiser here would freeze whichever
  // value existed at RoundEditor's first render, forever.
  orchards: OrchardOption[];
  fertilizers: FertilizerProduct[];
  onFertilizerCreated: (f: FertilizerProduct) => void;
}

const RoundEditor: React.FC<RoundEditorProps> = ({
  programmeId,
  programme,
  initialRound,
  orchards,
  fertilizers,
  onFertilizerCreated,
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [round, setRound] = useState<EditableRound>(() => hydrateFoliarRound(initialRound));
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(
    initialRound.groups?.[0]?.groupId ?? null,
  );
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState('');

  // The active-programme lock, mirroring FoliarProgrammeEditorPage's `locked`.
  // Without it the lock was bypassable in one click: the rounds panel leaves
  // "Edit round" live even when the programme editor is read-only, so an
  // active programme's dose lines could be changed and saved from here. Viewing
  // stays fully available — the fields render, they just cannot be edited or
  // saved — so hiding "Edit round" instead would have cost the ability to
  // inspect a locked round. The backend enforces the same rule independently
  // (updateProgrammeRound → PROGRAMME_ACTIVE → 409); this is the UI half.
  const locked = programme.status === 'active';

  // Bulk-add-orchards modal state (reuse of NutritionProgrammeFormPage:1199-1293).
  const [bulkAddFor, setBulkAddFor] = useState<{ groupId: string } | null>(null);
  const [bulkQuery, setBulkQuery] = useState('');
  const [bulkChecked, setBulkChecked] = useState<string[]>([]);

  // Quick-create-product modal state.
  const [creatingFor, setCreatingFor] = useState<{ groupId: string; idx: number } | null>(null);
  const [createName, setCreateName] = useState('');
  const [createError, setCreateError] = useState('');
  const [creatingBusy, setCreatingBusy] = useState(false);

  // ── keep a valid group selected as groups are added/removed ─────────────
  useEffect(() => {
    if (selectedGroupId && round.groups.some((g) => g.groupId === selectedGroupId)) return;
    setSelectedGroupId(round.groups[0]?.groupId ?? null);
  }, [round, selectedGroupId]);

  // `hydrateFoliarRound(initialRound)` is recomputed on every render, but
  // useFormDirty's ref only captures whatever value it receives on THIS
  // component's first render — which, because RoundEditor only mounts once
  // the round is already loaded, is the real hydrated round, not null.
  const { dirty } = useFormDirty(hydrateFoliarRound(initialRound), round);
  useBeforeUnloadWarning(dirty);

  // ── field mutators ────────────────────────────────────────────────────
  const setField = (field: 'name' | 'month' | 'plannedStart', value: string) =>
    setRound((prev) => ({ ...prev, [field]: value }));
  const setWaterRate = (value: string) => setRound((prev) => ({ ...prev, waterRatePerHa: value }));
  // No `setType`: the application type is not a choice on this page — see
  // `hydrateFoliarRound`.

  // ── group mutators (groups are round-owned) ──────────────────────────
  const mutateGroup = (groupId: string, fn: (g: EditableGroup) => EditableGroup) =>
    setRound((prev) => ({
      ...prev,
      groups: prev.groups.map((g) => (g.groupId === groupId ? fn(g) : g)),
    }));

  const addGroup = () => {
    const groupId = nextGroupId(round);
    setSelectedGroupId(groupId);
    setRound((prev) => ({
      ...prev,
      groups: [...prev.groups, { groupId, label: '', orchardIds: [], products: [] }],
    }));
  };
  const removeGroup = (groupId: string) =>
    setRound((prev) => ({ ...prev, groups: prev.groups.filter((g) => g.groupId !== groupId) }));
  const setGroupLabel = (groupId: string, label: string) => mutateGroup(groupId, (g) => ({ ...g, label }));
  const addOrchardsToGroup = (groupId: string, codes: string[]) =>
    mutateGroup(groupId, (g) => ({
      ...g,
      orchardIds: [...g.orchardIds, ...codes.filter((c) => !g.orchardIds.includes(c))],
    }));
  const removeOrchardFromGroup = (groupId: string, code: string) =>
    mutateGroup(groupId, (g) => ({ ...g, orchardIds: g.orchardIds.filter((c) => c !== code) }));

  const openBulkAdd = (groupId: string) => {
    setBulkQuery('');
    setBulkChecked([]);
    setBulkAddFor({ groupId });
  };
  const closeBulkAdd = () => {
    setBulkAddFor(null);
    setBulkQuery('');
    setBulkChecked([]);
  };

  // ── product mutators (per group) ─────────────────────────────────────
  const mutateProducts = (groupId: string, fn: (ps: EditableProductLine[]) => EditableProductLine[]) =>
    mutateGroup(groupId, (g) => ({ ...g, products: fn(g.products) }));
  const addProduct = (groupId: string) => {
    // Always foliar on this page (`hydrateFoliarRound`), so the basis default
    // is per-100 L unconditionally — the old `round.type === 'foliar' ? … :
    // 'perTree'` branch can no longer be reached.
    const defaultBasis = 'per100L';
    mutateProducts(groupId, (ps) => [
      ...ps,
      { productId: '', productName: '', unit: '', doseBasis: defaultBasis, value: '' },
    ]);
  };
  const removeProduct = (groupId: string, idx: number) =>
    mutateProducts(groupId, (ps) => ps.filter((_, i) => i !== idx));
  const setProductField = (
    groupId: string,
    idx: number,
    field: 'productId' | 'unit' | 'doseBasis',
    value: string,
  ) => mutateProducts(groupId, (ps) => ps.map((p, i) => (i === idx ? { ...p, [field]: value } : p)));
  const setProductPatch = (groupId: string, idx: number, patch: Partial<EditableProductLine>) =>
    mutateProducts(groupId, (ps) => ps.map((p, i) => (i === idx ? { ...p, ...patch } : p)));
  const setProductValue = (groupId: string, idx: number, value: string) =>
    mutateProducts(groupId, (ps) => ps.map((p, i) => (i === idx ? { ...p, value } : p)));

  const openQuickCreate = (groupId: string, idx: number) => {
    setCreateName('');
    setCreateError('');
    setCreatingFor({ groupId, idx });
  };
  const closeQuickCreate = () => {
    setCreatingFor(null);
    setCreateName('');
    setCreateError('');
  };
  const handleCreateProduct = async () => {
    const name = createName.trim();
    if (!name || creatingBusy || !creatingFor) return;
    setCreatingBusy(true);
    setCreateError('');
    try {
      const created = await nutritionService.createFertilizer({
        productName: name,
        analysis: { n: 0, p: 0, k: 0 },
        applicationMethods: [],
      });
      onFertilizerCreated(created);
      setProductPatch(creatingFor.groupId, creatingFor.idx, {
        productId: created._id,
        productName: created.productName,
      });
      closeQuickCreate();
    } catch {
      // Error path 4 (GC1): a page-level Notice here would render UNDER this
      // open modal's scrim/focus-trap and be invisible to the user (the exact
      // hazard called out for this task) — so the message renders inside
      // Modal.Body instead.
      setCreateError(t('nutrition.programme.form.createProductError'));
    } finally {
      setCreatingBusy(false);
    }
  };

  // ── validation / save ─────────────────────────────────────────────────
  const errors = roundEditorErrors(round);

  const handleSave = async () => {
    if (!canSaveRound(round) || saving || locked) return;
    setSaving(true);
    setApiError('');
    try {
      await nutritionService.updateProgrammeRound(programmeId, round.roundId, buildRound(round));
      navigate(programmeEditorPath(programmeId));
    } catch {
      // Error path 3: save failure. No modal can be open while Save is being
      // clicked (Save lives in the sticky SaveBar, not inside a modal), so
      // this is visible without GC1's stacking hazard.
      setApiError(t('nutrition.foliar.round.saveError'));
    } finally {
      setSaving(false);
    }
  };
  const handleCancel = () => navigate(programmeEditorPath(programmeId));

  const saveBarStatus: SaveBarStatus = saving
    ? { kind: 'saving' }
    : apiError
      ? { kind: 'error', message: apiError }
      : dirty
        ? { kind: 'dirty' }
        : null;

  // page-patterns.md:204 reserves the short-form (no-TOC) variant for forms
  // with ≤6 fields and no logical sections. This page has two, so it needs
  // the long-form TOC/scroll-spy — omitting `sections` leaves the
  // `id="round-detail"` / `id="round-groups"` anchors pointing at nothing.
  const sections: FormShellSection[] = [
    { id: 'round-detail', title: t('nutrition.foliar.round.detailsTitle') },
    { id: 'round-groups', title: t('nutrition.programme.form.sections.groups') },
  ];

  return (
    <>
      <div className="ntr-sticky-header">
        <PageHeader
          title={round.name}
          meta={`${programme.season}${round.month ? ` · ${formatMonthName(Number(round.month), i18n.language)}` : ''}`}
        />
      </div>
      {locked && (
        <Notice tone="warn" testId="fre-locked-notice">
          {t('nutrition.foliar.round.lockedNotice')}
        </Notice>
      )}
      <FormShell
        sections={sections}
        saveBar={
          <SaveBar
            status={saveBarStatus}
            // Locked: the primary action is ABSENT, not merely disabled —
            // matching FoliarProgrammeEditorPage's Ruling-1 treatment. Cancel
            // stays, since leaving the page is not a write.
            primaryAction={locked ? undefined : {
              label: t('nutrition.foliar.round.save'),
              onClick: handleSave,
              disabled: !canSaveRound(round) || saving,
            }}
            secondaryActions={[
              {
                label: t('nutrition.programme.form.cancel'),
                tone: 'subtle',
                onClick: handleCancel,
                disabled: saving,
              },
            ]}
          />
        }
      >
        <FormSection id="round-detail" title={t('nutrition.foliar.round.detailsTitle')}>
          <div className="pat-field-grid pat-field-grid--2">
            <Field
              label={t('nutrition.programme.form.roundName')}
              required
              controlId="fre-name"
              // A blank name greys Save out; without this the user got no
              // message anywhere explaining why (`nameRequired` was computed
              // and then never rendered).
              errorText={errors.nameRequired ? t('nutrition.programme.form.roundNameRequired') : undefined}
            >
              <input
                id="fre-name"
                className="pat-input"
                value={round.name}
                required
                disabled={locked}
                onChange={(e) => setField('name', e.target.value)}
              />
            </Field>
            <Field label={t('nutrition.programme.form.month')} controlId="fre-month">
              <input
                id="fre-month"
                type="number"
                min={1}
                max={12}
                className="pat-input"
                value={round.month}
                disabled={locked}
                onChange={(e) => setField('month', e.target.value)}
              />
            </Field>
            <Field
              label={t('nutrition.foliar.round.plannedStart')}
              required
              controlId="fre-planned-start"
              errorText={errors.plannedStartRequired ? t('nutrition.foliar.round.plannedStartRequired') : undefined}
            >
              <input
                id="fre-planned-start"
                type="date"
                className="pat-input"
                value={round.plannedStart}
                disabled={locked}
                onChange={(e) => setField('plannedStart', e.target.value)}
              />
            </Field>
            <Field
              label={t('nutrition.programme.form.waterRate')}
              required
              controlId="fre-water"
              errorText={errors.waterRateRequired ? t('nutrition.programme.form.waterRateRequired') : undefined}
            >
              <input
                id="fre-water"
                type="number"
                min={0}
                step="any"
                className="pat-input"
                value={round.waterRatePerHa}
                disabled={locked}
                onChange={(e) => setWaterRate(e.target.value)}
              />
            </Field>
          </div>
        </FormSection>

        <FormSection id="round-groups" title={t('nutrition.programme.form.sections.groups')}>
          {/* A unit-less product line greys Save out. The offending line can
              sit in a group tab that is not the open one, so a per-line
              message alone would be invisible — this names the products
              instead, at a level where every group's lines are covered. */}
          {errors.missingUnitProducts.length > 0 && (
            <Notice tone="danger" testId="fre-missing-units">
              {t('nutrition.programme.form.missingUnitLines', {
                products: errors.missingUnitProducts.join(', '),
              })}
            </Notice>
          )}
          {round.groups.length === 0 ? (
            <>
              <Notice tone="info">{t('nutrition.programme.form.noGroups')}</Notice>
              {!locked && (
                <button type="button" className="pat-btn pat-btn--subtle" onClick={addGroup}>
                  {t('nutrition.programme.form.addGroup')}
                </button>
              )}
            </>
          ) : (
            <>
              <TabBar
                activeKey={selectedGroupId ?? round.groups[0].groupId}
                onSelect={setSelectedGroupId}
                ariaLabel={t('nutrition.programme.form.sections.groups')}
              >
                {round.groups.map((g, gi) => (
                  <TabPanel
                    key={g.groupId}
                    id={g.groupId}
                    title={g.label.trim() || `${t('nutrition.programme.form.groupName')} ${gi + 1}`}
                  >
                    <div className="ntr-group-editor">
                      <div className="pat-field-grid pat-field-grid--2">
                        <Field
                          label={t('nutrition.programme.form.groupName')}
                          controlId={`fre-grp-${g.groupId}`}
                        >
                          <input
                            id={`fre-grp-${g.groupId}`}
                            className="pat-input"
                            value={g.label}
                            disabled={locked}
                            onChange={(e) => setGroupLabel(g.groupId, e.target.value)}
                          />
                        </Field>
                        {!locked && (
                          <div className="ntr-fre-remove-wrap">
                            <button
                              type="button"
                              className="pat-btn pat-btn--subtle"
                              onClick={() => removeGroup(g.groupId)}
                            >
                              {t('nutrition.programme.form.remove')}
                            </button>
                          </div>
                        )}
                      </div>

                      <div className="ntr-section-label">
                        {t('nutrition.programme.form.orchards')}
                        <span className="ntr-muted">
                          {' · '}
                          {t('nutrition.programme.form.orchardsAssigned', {
                            n: g.orchardIds.length,
                            total: orchards.length,
                          })}
                        </span>
                      </div>
                      <div className="ntr-chips">
                        {g.orchardIds.map((code) => (
                          <span key={code} className="ntr-chip">
                            {code}
                            {!locked && (
                              <button
                                type="button"
                                className="ntr-chip__x"
                                aria-label={`${t('nutrition.programme.form.remove')} ${code}`}
                                onClick={() => removeOrchardFromGroup(g.groupId, code)}
                              >
                                ×
                              </button>
                            )}
                          </span>
                        ))}
                        {!locked && (
                          <button
                            type="button"
                            className="pat-btn pat-btn--subtle ntr-bulk-add-btn"
                            onClick={() => openBulkAdd(g.groupId)}
                          >
                            {t('nutrition.programme.form.bulkAdd')}
                          </button>
                        )}
                      </div>

                      <div className="ntr-section-label">{t('nutrition.programme.form.products')}</div>
                      {g.products.map((p, idx) => (
                        <div key={idx} className="ntr-fre-product-row">
                          <Field
                            label={t('nutrition.programme.form.productId')}
                            controlId={`fre-prod-${g.groupId}-${idx}`}
                          >
                            <select
                              id={`fre-prod-${g.groupId}-${idx}`}
                              className="pat-input"
                              value={p.productId}
                              disabled={locked}
                              onChange={(e) => {
                                if (e.target.value === '__create__') {
                                  openQuickCreate(g.groupId, idx);
                                  return;
                                }
                                const picked = fertilizers.find((f) => f._id === e.target.value);
                                setProductPatch(g.groupId, idx, {
                                  productId: e.target.value,
                                  productName: picked?.productName,
                                });
                              }}
                            >
                              <option value="">{t('nutrition.programme.form.selectProduct')}</option>
                              {fertilizers.map((f) => (
                                <option key={f._id} value={f._id}>
                                  {f.productName}
                                </option>
                              ))}
                              <option value="__create__">{t('nutrition.programme.form.newProduct')}</option>
                            </select>
                          </Field>
                          <Field
                            label={t('nutrition.programme.form.unit')}
                            controlId={`fre-unit-${g.groupId}-${idx}`}
                          >
                            <select
                              id={`fre-unit-${g.groupId}-${idx}`}
                              className="pat-input"
                              value={p.unit}
                              disabled={locked}
                              onChange={(e) => setProductField(g.groupId, idx, 'unit', e.target.value)}
                            >
                              <option value="">{t('units.selectPlaceholder')}</option>
                              {(() => {
                                const fam = familyOf(
                                  fertilizers.find((f) => f._id === p.productId)?.stockUnit,
                                );
                                const baseOpts = fam ? UNITS_BY_FAMILY[fam] : MEASURE_UNITS;
                                // Off-family legacy unit already on the line: append it rather than
                                // blanking it out from under the user (two dedicated tests pin this).
                                return p.unit && !baseOpts.includes(p.unit) ? [...baseOpts, p.unit] : baseOpts;
                              })().map((u) => (
                                <option key={u} value={u}>
                                  {u}
                                </option>
                              ))}
                            </select>
                          </Field>
                          <Field
                            label={t('nutrition.programme.form.doseBasis')}
                            controlId={`fre-basis-${g.groupId}-${idx}`}
                          >
                            <select
                              id={`fre-basis-${g.groupId}-${idx}`}
                              className="pat-input"
                              value={p.doseBasis}
                              disabled={locked}
                              onChange={(e) => setProductField(g.groupId, idx, 'doseBasis', e.target.value)}
                            >
                              <option value="perTree">{t('nutrition.programme.form.basisPerTree')}</option>
                              <option value="per100L">{t('nutrition.programme.form.basisPer100L')}</option>
                            </select>
                          </Field>
                          <Field
                            label={t('nutrition.programme.form.dose')}
                            controlId={`fre-dose-${g.groupId}-${idx}`}
                          >
                            <input
                              id={`fre-dose-${g.groupId}-${idx}`}
                              type="number"
                              min={0}
                              step="any"
                              className="pat-input"
                              value={p.value}
                              disabled={locked}
                              onChange={(e) => setProductValue(g.groupId, idx, e.target.value)}
                            />
                          </Field>
                          {!locked && (
                            <button
                              type="button"
                              className="pat-btn pat-btn--subtle"
                              onClick={() => removeProduct(g.groupId, idx)}
                            >
                              {t('nutrition.programme.form.remove')}
                            </button>
                          )}
                        </div>
                      ))}
                      {!locked && (
                        <button
                          type="button"
                          className="pat-btn pat-btn--subtle"
                          onClick={() => addProduct(g.groupId)}
                        >
                          {t('nutrition.programme.form.addProduct')}
                        </button>
                      )}
                    </div>
                  </TabPanel>
                ))}
              </TabBar>
              {!locked && (
                <button type="button" className="pat-btn pat-btn--subtle" onClick={addGroup}>
                  {t('nutrition.programme.form.addGroup')}
                </button>
              )}
            </>
          )}
        </FormSection>

        <Modal
          show={creatingFor !== null}
          onHide={closeQuickCreate}
          title={t('nutrition.programme.form.createProductTitle')}
          size="sm"
        >
          <Modal.Body>
            {createError && (
              <Notice tone="danger" className="mb-3">
                {createError}
              </Notice>
            )}
            <Field
              label={t('nutrition.programme.form.createProductName')}
              required
              controlId="fre-create-name"
            >
              <input
                id="fre-create-name"
                type="text"
                className="pat-input"
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
              />
            </Field>
          </Modal.Body>
          <Modal.Footer>
            <button
              type="button"
              className="pat-btn pat-btn--subtle"
              onClick={closeQuickCreate}
              disabled={creatingBusy}
            >
              {t('nutrition.programme.form.cancel')}
            </button>
            <button
              type="button"
              className="pat-btn pat-btn--primary"
              onClick={handleCreateProduct}
              disabled={creatingBusy || !createName.trim()}
            >
              {t('nutrition.programme.form.create')}
            </button>
          </Modal.Footer>
        </Modal>

        {(() => {
          if (!bulkAddFor) return null;
          const bg = round.groups.find((g) => g.groupId === bulkAddFor.groupId);
          if (!bg) return null;
          const pool = availableOrchards(round, bg.groupId, orchards.map((o) => o.orchardName)).filter(
            (c) => !bg.orchardIds.includes(c),
          );
          const q = bulkQuery.trim().toLowerCase();
          const filtered = q ? pool.filter((c) => c.toLowerCase().includes(q)) : pool;
          const allFilteredChecked =
            filtered.length > 0 && filtered.every((c) => bulkChecked.includes(c));
          const toggleSelectAll = () =>
            setBulkChecked((prev) =>
              allFilteredChecked
                ? prev.filter((c) => !filtered.includes(c))
                : Array.from(new Set([...prev, ...filtered])),
            );
          const toggleOne = (c: string) =>
            setBulkChecked((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));
          return (
            <Modal
              show
              onHide={closeBulkAdd}
              title={t('nutrition.programme.form.bulkAddTitle', {
                group: bg.label.trim() || bg.groupId,
              })}
            >
              <Modal.Body>
                <input
                  type="text"
                  className="pat-input"
                  placeholder={t('nutrition.programme.form.bulkSearch')}
                  value={bulkQuery}
                  onChange={(e) => setBulkQuery(e.target.value)}
                  aria-label={t('nutrition.programme.form.bulkSearch')}
                />
                {filtered.length === 0 ? (
                  <p className="ntr-subcard__meta" style={{ marginTop: 10 }}>
                    {t('nutrition.programme.form.bulkNone')}
                  </p>
                ) : (
                  <>
                    <label className="ntr-bulk-row ntr-bulk-row--all">
                      <input type="checkbox" checked={allFilteredChecked} onChange={toggleSelectAll} />
                      <span>{t('nutrition.programme.form.selectAll')}</span>
                    </label>
                    <div className="ntr-bulk-list">
                      {filtered.map((c) => (
                        <label key={c} className="ntr-bulk-row">
                          <input type="checkbox" checked={bulkChecked.includes(c)} onChange={() => toggleOne(c)} />
                          <span>{c}</span>
                        </label>
                      ))}
                    </div>
                  </>
                )}
              </Modal.Body>
              <Modal.Footer>
                <button type="button" className="pat-btn pat-btn--subtle" onClick={closeBulkAdd}>
                  {t('nutrition.programme.form.cancel')}
                </button>
                <button
                  type="button"
                  className="pat-btn pat-btn--primary"
                  disabled={bulkChecked.length === 0}
                  onClick={() => {
                    addOrchardsToGroup(bulkAddFor.groupId, bulkChecked);
                    closeBulkAdd();
                  }}
                >
                  {t('nutrition.programme.form.bulkAddConfirm', { n: bulkChecked.length })}
                </button>
              </Modal.Footer>
            </Modal>
          );
        })()}
      </FormShell>
    </>
  );
};
